import { FC, useCallback, useContext, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ArrowForward } from '@mui/icons-material';
import { Box, Card, Stack, Typography, useMediaQuery, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import uniqueId from 'lodash/uniqueId';

import { useRouter } from '@cvt/routing';
import { DialogContext } from '@cvt/contexts/DialogContext/DialogContext';
import { FeedbackContext } from '@cvt/contexts';
import { useDictionary } from '@cvt/hooks/useDictionary';

import { TimelineForm } from '@modules/Tributes/components/TimelineForm';
import { useMe } from '@modules/Auth/hooks/useMe';
import { useTributeCrud } from '@modules/Tributes/hooks/useTributeCrud';
import { useFormDataTransform } from '@modules/Tributes/hooks/useFormDataTransform';
import { useCheckoutSession } from '@modules/Payments/useCheckoutSession';
import { validateIfPromoCodeIsValid } from '@modules/Tributes/helpers/validators';

import { TributeFormData } from '../CreateTribute/CreateTribute';


type FormData = Tributes.FormData;

interface Props {
  onComplete: () => void;
  initialData?: Tributes.Tribute;
  isEditing?: boolean;
  isCreating?: boolean;
}

export const PreviewStep: FC<Props> = (props) => {
  const { initialData, isEditing = false, isCreating = false } = props;
  const router = useRouter();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const { createTribute, editTribute, payWithCredit } = useTributeCrud();
  const { toTributeCreateCrud } = useFormDataTransform();
  const dictionary = useDictionary();

  const { user } = useMe();

  const { openDialog, closeDialog, openedDialogs } = useContext(DialogContext);
  const { genericErrorFeedback, triggerFeedback } = useContext(FeedbackContext);

  const { getValues } = useFormContext<TributeFormData>();

  const clickedButtonRef = useRef<'credit' | 'checkout' | null>(null);

  const { createSession, validatePromoCode } = useCheckoutSession();
  const tributePrice: string = import.meta.env.VITE__CVT_TRIBUTE_PRICE_ID;

  const defaultFormValues: FormData & { _key: string } = useMemo(() => {
    if (isEditing && initialData) {
      const pet = {
        petType: initialData.pet.petType || '',
        sex: initialData.pet.sex || '',
        name: initialData.pet.name || '',
        birthday: initialData.pet.birthday,
        passingDate: initialData.pet.passingDate,
        favouriteMemory: initialData.pet.favouriteMemory || '',
        personality: initialData.pet.personality || '',
        specialHabits: initialData.pet.specialHabits || '',
      };
      const mainPhoto = initialData.mainPhoto ? initialData.mainPhoto : null;
      const timelineEvents = initialData.memories.map(memory => ({
        media: memory.media || [],
        date: memory.date || new Date(),
        text: memory.text || '',
      }));

      return {
        _key: `${uniqueId()}_${initialData.id}`, // Include tribute ID in the key to ensure it changes when initialData changes
        pet,
        timeline: timelineEvents.map(event => ({
          saved: { ...event },
          draft: { ...event },
        })),
        mainPhoto,
      };
    } else {
      // Get all form values at once
      const formValues = getValues();

      const pet = {
        petType: formValues.petType || '',
        name: formValues.petName || '',
        sex: formValues.sex || '',
        birthday: formValues.petDateOfBirth || null,
        passingDate: formValues.petDateOfPassing || null,
        favouriteMemory: formValues.favouriteMemory || '',
        personality: formValues.personality || '',
        specialHabits: formValues.specialHabits || '',
      };

      // Use mainPhoto from form context
      const mainPhotoFromContext = formValues.mainPhoto || null;

      // Use mediaFiles from form context, excluding the main photo
      const mediaFilesFromContext = formValues.mediaFiles || [];
      const restFiles = mainPhotoFromContext ? mediaFilesFromContext.filter(file => file !== mainPhotoFromContext) : mediaFilesFromContext;

      const timelineEventsFromFiles = restFiles.map(file => ({
        media: [file],
        date: new Date(file.lastModified),
        text: '',
      }));

      const timelineEvents = [...timelineEventsFromFiles];

      const userData = {
        firstName: formValues.firstName,
        email: formValues.email,
        phone: formValues.phone,
      };

      return {
        _key: uniqueId(),
        pet,
        timeline: timelineEvents.map(event => ({
          saved: { ...event },
          draft: { ...event },
        })),
        mainPhoto: mainPhotoFromContext,
        userData,
      };
    }
  }, [getValues, initialData, isEditing]);

  const createStripeSession = useCallback(async (data: { tributeId: string; promotionCode?: string }) => {
    const { tributeId, promotionCode } = data;
    try {
      const checkoutPayload = {
        quantity: 1,
        price_id: tributePrice,
        tribute_id: tributeId,
        promotion_code: promotionCode,
      };
      const { url } = await createSession.mutateAsync(checkoutPayload);
      window.location.assign(url);
    } catch (error) {
      console.error('Error creating Stripe session:', error);

    }
  }, [createSession, tributePrice]);

  const saveTribute = useCallback(async (data: FormData) => {
    try {
      const tributeData = await toTributeCreateCrud(data);

      if (isEditing && initialData) {
        const editPayload = {
          ...tributeData,
          id: initialData.id,
        };
        await editTribute.mutateAsync(editPayload);

        const submissionType = clickedButtonRef.current;

        if (submissionType === 'credit') {
          await payWithCredit.mutateAsync(initialData.id);
          router.tributes.view(initialData.id).go();
        } else if (submissionType === 'checkout') {
          await createStripeSession({ tributeId: initialData.id, promotionCode: data.promotionCode });
        } else {
          router.tributes.myTributes.go();
        }
      } else {
        const submissionType = clickedButtonRef.current;
        if (submissionType === 'credit') {
          const { data: tribute } = await createTribute.mutateAsync(tributeData);
          await payWithCredit.mutateAsync(tribute.id);
          router.tributes.view(tribute.id).go();
        } else if (submissionType === 'checkout') {
          const { data: tribute } = await createTribute.mutateAsync(tributeData);
          await createStripeSession({ tributeId: tribute.id, promotionCode: data.promotionCode });
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.media?.[0][0];
      if (errorMessage.startsWith('File size cannot exceed')) {
        triggerFeedback({
          message: errorMessage,
          severity: 'error',
        });
      } else {
        genericErrorFeedback();
      }
    } finally {
      closeDialog('savingTribute');
    }
  }, [toTributeCreateCrud, isEditing, initialData, editTribute, payWithCredit, router, createStripeSession, createTribute, triggerFeedback, closeDialog, genericErrorFeedback]);

  const onSubmit = useCallback(async (data: FormData) => {
    await saveTribute(data);
  }, [saveTribute]);

  return (
    <Stack maxWidth="sm" margin="0 auto" sx={{ py: 2, px: { xs: 0, sm: 2 } }}>
      <Card sx={{
        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgb(254, 238, 237)',
        borderRadius: '12px',
        overflow: 'visible',
        pt: 4,
        pb: 2,
      }}
      >
        <Stack
          alignItems="center"
          flexGrow={1}
          gap={2}
          mx={2}
        >
          <Typography variant="h1" align="center" fontSize={24} fontWeight={700}>
            Build Their LGCY
          </Typography>
          <Typography variant="body1" color="textSecondary" align="center" fontSize={14} fontWeight={500}>
            It's easy to add to their LGCY. Change the date to reorder memories, write their stories, and upload photos or videos. Need help with a story? Just upload a photo and Fluffy, our AI assistant, will describe it for you.
          </Typography>
        </Stack>

        <Box mx={{ xs: 0, sm: 2 }}>
          <TimelineForm
            onSubmitRequest={onSubmit}
            readonly={!!openedDialogs['savingTribute']}
            key={defaultFormValues._key}
            defaultValues={defaultFormValues}
            isEditing={isEditing}
            isCreating={isCreating}
            slots={{
              footer: (formState, { control, trigger }) => (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-around"
                  alignItems={ isEditing ? 'flex-start' : 'center'}
                  mt={4}
                  mx={{ xs: 2, sm: 0 }}
                  spacing={1}
                >
                  {isEditing && initialData?.status === 'published' ? (
                    <LoadingButton
                      variant="outlined"
                      color="primary"
                      type="submit"
                      fullWidth={isMobile}
                      onClick={() => {
                        clickedButtonRef.current = null;
                        openDialog('savingTribute');
                      }}
                      disabled={!!openedDialogs['savingTribute'] || formState.isSubmitting}
                      sx={{
                        px: 3,
                        py: 1,
                      }}
                    >
                      {dictionary.forms.buttonEdit}
                    </LoadingButton>
                  ) : (
                    <>
                      {(user?.credits?.length ?? 0) > 0 && (
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          type="submit"
                          fullWidth={isMobile}
                          onClick={() => {
                            clickedButtonRef.current = 'credit';
                            openDialog('savingTribute');
                          }}
                          disabled={!!openedDialogs['savingTribute'] || formState.isSubmitting}
                          sx={{
                            px: 3,
                            py: 1,
                          }}
                          endIcon={<ArrowForward />}
                        >
                          {isEditing ? 'Save & Pay with Credit' : 'Publish using credit'}
                        </LoadingButton>
                      )}
                      <Stack direction={isMobile && isEditing ? 'column' : 'row'} sx={{ width: isMobile && isEditing ? '100%' : 'auto' }} alignItems="flex-start">
                        <Controller
                          name="promotionCode"
                          control={control}
                          rules={{
                            validate: (promoCode) => {
                              return validateIfPromoCodeIsValid(promoCode, validatePromoCode, dictionary);
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              variant="outlined"
                              color="primary"
                              placeholder="Promo code"
                              fullWidth
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: '100%',
                                },
                              }}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              slotProps={{
                                input: {
                                  sx: {
                                    fieldset: {
                                      borderColor: (theme) => theme.palette.primary.main,
                                      borderTopLeftRadius: isEditing && isMobile ? 0 : 4,
                                      borderBottomLeftRadius: isEditing && isMobile ? 0 : 4,
                                    },
                                  },
                                },
                              }}
                            />
                          )}
                        />
                        <LoadingButton
                          variant="contained"
                          color="primary"
                          type="submit"
                          fullWidth={isMobile}
                          onClick={async (event) => {
                            const isValid = await trigger();
                            if (!isValid) {
                              event.preventDefault();
                              return;
                            }
                            clickedButtonRef.current = 'checkout';
                            openDialog('savingTribute');
                          }}
                          disabled={!!openedDialogs['savingTribute'] || formState.isSubmitting}
                          sx={{
                            px: 3,
                            py: 1,
                            flexShrink: isEditing ? 0 : undefined,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: isEditing && isMobile ? 0 : undefined,
                            borderBottomLeftRadius: isEditing && isMobile ? undefined : 0,
                            height: 40,
                          }}
                          endIcon={<ArrowForward />}
                        >
                          {isEditing ? 'Save & Pay' : 'Publish'}
                        </LoadingButton>
                      </Stack>
                      {isEditing && (
                        <LoadingButton
                          variant="outlined"
                          color="primary"
                          type="submit"
                          fullWidth={isMobile}
                          onClick={() => {
                            clickedButtonRef.current = null;
                            formState.isValid && openDialog('savingTribute');
                          }}
                          disabled={!!openedDialogs['savingTribute'] || formState.isSubmitting}
                          sx={{
                            flexShrink: 0,
                            px: 3,
                            py: 1,
                          }}
                        >
                          {dictionary.forms.buttonEdit}
                        </LoadingButton>
                      )}
                    </>
                  )}
                </Stack>
              ),
            }}
          />
        </Box>
      </Card>
    </Stack>
  );
};
